import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AtSign, AlertTriangle, Check } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { setUsername as setUsernameApi } from '../../services/api';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 20px;
  padding: 32px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  border: 1px solid ${({ theme }) => theme.isDarkMode ? '#3a3b3c' : '#e4e6eb'};
`;

const Title = styled.h2`
  text-align: center;
  font-size: 20px;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.text};
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  margin-bottom: 24px;
  line-height: 1.4;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px 14px 44px;
  border: 2px solid ${({ theme, error }) => error ? '#e53935' : theme.isDarkMode ? '#3a3b3c' : '#e4e6eb'};
  border-radius: 12px;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.background};
  transition: border-color 0.2s;

  &:focus {
    border-color: ${({ theme, error }) => error ? '#e53935' : theme.colors.primary};
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorText = styled.div`
  color: #e53935;
  font-size: 13px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SubmitButton = styled.button`
  background: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : '#181A1B'};
  color: ${({ theme }) => theme.isDarkMode ? '#181A1B' : '#FFFFFF'};
  padding: 14px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  width: 100%;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.isDarkMode ? '#81c784' : '#2e7d32'};
  font-size: 14px;
  padding: 16px;
`;

const SetUsername = ({ onComplete, initialError }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [username, setUsername] = useState('');
  const [error, setError] = useState(initialError || '');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = (value) => {
    if (value.length < 3) return t('auth.usernameMinLength') || 'Минимум 3 символа';
    if (value.length > 30) return t('auth.usernameMaxLength') || 'Максимум 30 символов';
    if (!/^[a-zA-Z0-9_]+$/.test(value)) return t('auth.usernameInvalidChars') || 'Только латиница, цифры и _';
    return '';
  };

  const handleChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
    setUsername(value);
    if (error) setError(validate(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate(username);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await setUsernameApi(username);
      onComplete(result);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Ошибка при установке username');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay>
      <Modal theme={theme}>
        {success ? (
          <SuccessMessage theme={theme}>
            <Check size={32} style={{ marginBottom: 12 }} />
            <br />
            {t('auth.usernameSetSuccess') || 'Username успешно установлен!'}
          </SuccessMessage>
        ) : (
          <>
            <Title theme={theme}>{t('auth.chooseUsername') || 'Придумайте никнейм'}</Title>
            <Subtitle theme={theme}>
              {t('auth.chooseUsernameHint') || 'Придумайте уникальное имя пользователя, которое будут видеть другие'}
            </Subtitle>

            <form onSubmit={handleSubmit}>
              <InputGroup>
                <InputIcon theme={theme}>
                  <AtSign size={18} />
                </InputIcon>
                <Input
                  type="text"
                  placeholder={t('auth.usernamePlaceholder') || 'Ваш никнейм'}
                  value={username}
                  onChange={handleChange}
                  error={!!error}
                  autoFocus
                  maxLength={30}
                  theme={theme}
                />
              </InputGroup>

              {error && (
                <ErrorText>
                  <AlertTriangle size={14} />
                  {error}
                </ErrorText>
              )}

              <SubmitButton type="submit" disabled={loading || username.length < 3} theme={theme}>
                {loading ? (t('auth.saving') || 'Сохранение...') : (t('auth.saveUsername') || 'Сохранить')}
              </SubmitButton>
            </form>
          </>
        )}
      </Modal>
    </Overlay>
  );
};

export default SetUsername;